/**
 * PDP — variant selection, accordion, add-to-cart, recommendation add.
 */
(function () {
  'use strict';

  const money = (cents, currency) => {
    const amount = (cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const symbol = currency || '€';
    return `${symbol}${amount}`;
  };

  const initSection = (section) => {
    const variantsScript = section.querySelector('[data-product-variants]');
    if (!variantsScript) return;

    let variants = [];
    try { variants = JSON.parse(variantsScript.textContent); } catch (_) {}

    // Preorder is per-variant: a variant id present in this map is on preorder
    // (value = its formatted ship date). Built in Liquid with the product-level
    // date falling through to every variant, so whole-product preorder still works.
    // A preorder variant reads as buyable even when out of stock.
    let preorderMap = {};
    const preorderMapScript = section.querySelector('[data-variant-preorder-map]');
    if (preorderMapScript) {
      try { preorderMap = JSON.parse(preorderMapScript.textContent); } catch (_) {}
    }
    const preorderDateFor = (variant) => (variant && preorderMap[variant.id]) || null;

    // variant id → gallery media id (for switching the gallery when a colour is picked).
    let variantMediaMap = {};
    const mediaMapScript = section.querySelector('[data-variant-media-map]');
    if (mediaMapScript) {
      try { variantMediaMap = JSON.parse(mediaMapScript.textContent); } catch (_) {}
    }
    const mediaGallery = section.querySelector('[data-gallery]');

    const scrollToVariantMedia = (variantId) => {
      const mediaId = variantMediaMap[variantId];
      if (!mediaId || !mediaGallery) return;
      const slide = mediaGallery.querySelector(`[data-media-id="${mediaId}"]`);
      if (!slide) return;
      const isCarousel = window.getComputedStyle(mediaGallery).flexDirection === 'row';
      if (isCarousel) {
        mediaGallery.scrollTo({ left: slide.offsetLeft - mediaGallery.offsetLeft, behavior: 'smooth' });
      } else {
        slide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    const form = section.querySelector('[data-product-form]');
    const variantIdInput = section.querySelector('[data-variant-id-input]');
    const priceEl = section.querySelector('[data-product-price]');
    const comparePriceEl = section.querySelector('[data-product-compare-price]');
    const discountPriceEl = section.querySelector('[data-product-discount-price]');
    const submitBtn = section.querySelector('[data-add-to-cart]');
    const submitLabel = section.querySelector('[data-add-to-cart-label]');
    const inventoryStatus = section.querySelector('[data-inventory-status]');
    const inventoryAvailable = section.querySelector('[data-inventory-available]');
    const inventoryUnavailable = section.querySelector('[data-inventory-unavailable]');
    const inventoryPreorder = section.querySelector('[data-inventory-preorder]');
    const inventoryPreorderDate = section.querySelector('[data-preorder-date]');

    const stickyBar = section.querySelector('[data-sticky-bar]');
    const stickyPriceEl = section.querySelector('[data-sticky-price]');
    const stickyBtn = section.querySelector('[data-sticky-add-to-cart]');
    const stickyLabel = section.querySelector('[data-sticky-add-to-cart-label]');

    // ------------------------------------------------------------------
    // Variant selection
    // ------------------------------------------------------------------
    const getSelectedOptions = () => {
      const options = [];
      section.querySelectorAll('[data-option-position]:checked').forEach((input) => {
        const pos = parseInt(input.dataset.optionPosition, 10) - 1;
        options[pos] = input.value;
      });
      return options;
    };

    const findVariant = (selected) => {
      return variants.find((v) => {
        return (
          v.option1 === (selected[0] || null) &&
          (selected[1] === undefined || v.option2 === selected[1]) &&
          (selected[2] === undefined || v.option3 === selected[2])
        );
      });
    };

    const updateSelectedLabels = () => {
      section.querySelectorAll('[data-option-selected-for]').forEach((el) => {
        const pos = parseInt(el.dataset.optionSelectedFor, 10) - 1;
        const selected = getSelectedOptions();
        if (selected[pos] != null) el.textContent = selected[pos];
      });
    };

    const updateAvailability = (variant) => {
      // Cross out unavailable options for each option group (based on current selection)
      const selected = getSelectedOptions();
      section.querySelectorAll('.pdp__option').forEach((optionEl) => {
        optionEl.querySelectorAll('.pdp__option-input').forEach((input) => {
          // Real Shopify option position, NOT the DOM index: the cross-product
          // "Color" swatch row also renders a .pdp__option, which would shift the
          // index and mis-flag the size (e.g. "One Size") as sold out.
          const pos = parseInt(input.dataset.optionPosition, 10) - 1;
          const testValues = selected.slice();
          testValues[pos] = input.value;
          const match = variants.find((v) => {
            return (
              v.option1 === (testValues[0] || null) &&
              (testValues[1] === undefined || v.option2 === testValues[1]) &&
              (testValues[2] === undefined || v.option3 === testValues[2])
            );
          });
          // A variant on preorder is buyable even when out of stock — never strike it.
          const unavailable = !match || (!match.available && !preorderMap[match.id]);
          const label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', unavailable);
        });
      });
    };

    const updateURL = (variantId) => {
      if (!variantId || !window.history.replaceState) return;
      const url = new URL(window.location);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
    };

    const tProd = (window.theme && window.theme.t && window.theme.t.product) || {};

    // A sold-out buy button opens the Back in Stock ("Avísame") modal instead of
    // adding to cart — reuses the exact same widget as the floating BIS tab.
    const openBackInStock = () => {
      const trigger = document.querySelector('.BIS_trigger');
      if (trigger) {
        trigger.click();
        return true;
      }
      return false;
    };

    const applyVariant = (variant) => {
      if (!variant) {
        if (submitBtn) {
          submitBtn.disabled = true;
          if (submitLabel) submitLabel.textContent = tProd.unavailable || 'No disponible';
        }
        return;
      }

      if (variantIdInput) variantIdInput.value = variant.id;
      if (priceEl) priceEl.textContent = money(variant.price);
      if (stickyPriceEl) stickyPriceEl.textContent = money(variant.price);

      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = money(variant.compare_at_price);
          comparePriceEl.hidden = false;
        } else {
          comparePriceEl.hidden = true;
        }
      }

      if (discountPriceEl) {
        // Recompute based on original discount percentage from data attr
        const pct = parseInt(discountPriceEl.dataset.discountPercent || '0', 10);
        if (pct > 0) {
          const discounted = Math.round((variant.price * (100 - pct)) / 100);
          discountPriceEl.textContent = money(discounted);
        }
      }

      // Preorder is resolved for THIS variant (per-size), not the whole product.
      const vPreorderDate = preorderDateFor(variant);
      const vPreorder = Boolean(vPreorderDate);

      const labelFor = (el) => {
        if (!el) return '';
        if (vPreorder) return el.dataset.preorderLabel || el.textContent;
        if (variant.available) return el.dataset.availableLabel || el.textContent;
        return el.dataset.soldOutLabel || tProd.soldOut || 'Agotado';
      };

      // Sold out -> keep the button clickable so it can open the back-in-stock
      // modal (handled in the submit/click listeners), only fade it visually.
      const soldOut = !vPreorder && !variant.available;
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.dataset.soldOut = soldOut ? 'true' : 'false';
        submitBtn.classList.toggle('pdp__submit--sold-out', soldOut);
      }
      if (submitLabel) submitLabel.textContent = labelFor(submitLabel);

      if (stickyBtn) {
        stickyBtn.disabled = false;
        stickyBtn.dataset.soldOut = soldOut ? 'true' : 'false';
        stickyBtn.classList.toggle('pdp__sticky-submit--sold-out', soldOut);
      }
      if (stickyLabel) stickyLabel.textContent = labelFor(stickyLabel);

      // Toggle the inventory pill that matches this variant's state. Preorder
      // wins over stock; its date text is updated from the per-variant map.
      if (inventoryPreorder) {
        inventoryPreorder.hidden = !vPreorder;
        if (vPreorder && inventoryPreorderDate && vPreorderDate) {
          inventoryPreorderDate.textContent = vPreorderDate;
        }
      }
      if (inventoryAvailable) inventoryAvailable.hidden = vPreorder || !variant.available;
      if (inventoryUnavailable) inventoryUnavailable.hidden = vPreorder || variant.available;

      updateURL(variant.id);
    };

    // Listen for option changes.
    section.querySelectorAll('[data-option-position]').forEach((input) => {
      input.addEventListener('change', () => {
        const selected = getSelectedOptions();
        const variant = findVariant(selected);
        updateSelectedLabels();
        applyVariant(variant);
        updateAvailability(variant);
        if (variant) scrollToVariantMedia(variant.id);
      });
    });

    // Initialize availability state on load.
    updateAvailability(findVariant(getSelectedOptions()));

    // ------------------------------------------------------------------
    // Add-to-cart (AJAX)
    // ------------------------------------------------------------------
    if (form && submitBtn) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (submitBtn.dataset.soldOut === 'true') { openBackInStock(); return; }
        if (submitBtn.disabled) return;

        const variantId = variantIdInput && variantIdInput.value;
        if (!variantId) return;

        submitBtn.classList.add('is-loading');
        submitBtn.disabled = true;
        const originalLabel = submitLabel ? submitLabel.textContent : (tProd.add || 'Añadir');
        if (submitLabel) submitLabel.textContent = '…';

        const formData = new FormData(form);

        fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/javascript' },
        })
          .then((res) => {
            if (!res.ok) return res.json().then((err) => Promise.reject(err));
            return res.json();
          })
          .then(() => fetch('/cart.js').then((r) => r.json()))
          .then((cart) => {
            submitBtn.classList.remove('is-loading');
            submitBtn.disabled = false;
            if (submitLabel) submitLabel.textContent = tProd.added || 'Añadido';
            document.querySelectorAll('[data-cart-count]').forEach((el) => {
              el.textContent = cart.item_count;
              el.classList.toggle('visually-hidden', cart.item_count === 0);
            });
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
            window.setTimeout(() => {
              if (submitLabel) submitLabel.textContent = originalLabel;
            }, 1600);
          })
          .catch((err) => {
            console.error('[pdp] add-to-cart failed', err);
            submitBtn.classList.remove('is-loading');
            submitBtn.disabled = false;
            if (submitLabel) submitLabel.textContent = (err && err.description) || tProd.error || 'Error';
            window.setTimeout(() => {
              if (submitLabel) submitLabel.textContent = originalLabel;
            }, 2200);
          });
      });
    }

    // ------------------------------------------------------------------
    // Sticky add-to-cart bar — show when main buy button leaves the viewport
    // ------------------------------------------------------------------
    if (stickyBar && submitBtn) {
      const buyAnchor = section.querySelector('.pdp__buy') || submitBtn;

      if (stickyBtn) {
        const sizeOption = section.querySelector('.pdp__option--size');

        stickyBtn.addEventListener('click', () => {
          if (submitBtn.dataset.soldOut === 'true') { openBackInStock(); return; }
          if (submitBtn.disabled) return;

          if (sizeOption) {
            const header = document.querySelector('.susmies-header, .section-header, header[role="banner"]');
            const stickyBarHeight = stickyBar.offsetHeight || 0;
            const headerOffset = (header?.offsetHeight || 0) + 24;
            const rect = sizeOption.getBoundingClientRect();
            // Center the size option in the visible area between header and sticky bar.
            const visibleHeight = window.innerHeight - headerOffset - stickyBarHeight;
            const target = rect.top + window.scrollY - headerOffset - Math.max(0, (visibleHeight - rect.height) / 2);
            window.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
            sizeOption.classList.add('is-highlight');
            window.setTimeout(() => sizeOption.classList.remove('is-highlight'), 1200);
            return;
          }

          // Reuse the main button's submit pipeline (loading state, AJAX, toasts).
          submitBtn.click();
        });
      }

      if ('IntersectionObserver' in window) {
        const buyObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // Show whenever the main buy button isn't on screen — covers
              // both scrolling past it AND landing with it below the fold.
              const shouldShow = !entry.isIntersecting;
              stickyBar.classList.toggle('is-visible', shouldShow);
              stickyBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
            });
          },
          { threshold: 0, rootMargin: '0px 0px -20px 0px' }
        );
        buyObserver.observe(buyAnchor);
      }
    }

    // ------------------------------------------------------------------
    // Accordions — smooth height animation (matches FAQ section)
    // ------------------------------------------------------------------
    const ACCORDION_DURATION_OPEN = 280;
    const ACCORDION_DURATION_CLOSE = 220;
    const ACCORDION_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

    section.querySelectorAll('[data-accordion]').forEach((details) => {
      if (details.dataset.accordionAnimated === 'true') return;
      details.dataset.accordionAnimated = 'true';

      const summary = details.querySelector('summary');
      const content = details.querySelector('.pdp__accordion-content');
      if (!summary || !content) return;

      // While an open/close animation is running, ignore further taps — a second
      // tap during the ~280ms open used to immediately close what just opened.
      // settle() ALWAYS strips the inline height/overflow so the content can
      // never be left clipped, even if an animation is interrupted.
      let animation = null;
      let isAnimating = false;
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const canAnimate = typeof details.animate === 'function' && !prefersReduced;

      const settle = (openState) => {
        details.open = openState;
        details.classList.remove('is-closing');
        details.style.height = '';
        details.style.overflow = '';
        animation = null;
        isAnimating = false;
      };

      const run = (startHeight, endHeight, openState) => {
        if (!canAnimate) {
          settle(openState);
          return;
        }
        details.style.overflow = 'hidden';
        details.style.height = startHeight;
        animation = details.animate(
          { height: [startHeight, endHeight] },
          {
            duration: openState ? ACCORDION_DURATION_OPEN : ACCORDION_DURATION_CLOSE,
            easing: ACCORDION_EASING,
          }
        );
        animation.onfinish = () => settle(openState);
      };

      const expand = () => {
        isAnimating = true;
        // Measure the collapsed height first, then reveal content and animate up to it.
        const startHeight = `${details.offsetHeight}px`;
        details.style.overflow = 'hidden';
        details.style.height = startHeight;
        details.open = true;
        window.requestAnimationFrame(() => {
          const endHeight = `${summary.offsetHeight + content.offsetHeight}px`;
          run(startHeight, endHeight, true);
        });
      };

      const collapse = () => {
        isAnimating = true;
        details.classList.add('is-closing');
        const startHeight = `${details.offsetHeight}px`;
        const endHeight = `${summary.offsetHeight}px`;
        run(startHeight, endHeight, false);
      };

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (isAnimating) return; // guard: don't reverse an in-progress animation
        if (details.open) collapse();
        else expand();
      });
    });

    // The complementary recs section is rendered as a sibling of .pdp,
    // so query from the Shopify section wrapper to reach it.
    const sectionScope = section.closest('.shopify-section') || document;

    // ------------------------------------------------------------------
    // Recommendation "Añadir"
    // ------------------------------------------------------------------
    sectionScope.querySelectorAll('[data-rec-add]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const hasOnlyDefault = btn.dataset.hasOnlyDefaultVariant === 'true';
        const variantId = btn.dataset.variantId;
        const url = btn.dataset.productUrl;

        if (!hasOnlyDefault) {
          window.location.href = url;
          return;
        }
        if (!variantId) return;

        const original = btn.textContent;
        btn.disabled = true;
        btn.textContent = '…';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        })
          .then((r) => (r.ok ? r.json() : r.json().then((e) => Promise.reject(e))))
          .then(() => fetch('/cart.js').then((r) => r.json()))
          .then((cart) => {
            btn.textContent = tProd.added || 'Añadido';
            btn.classList.add('is-added');
            document.querySelectorAll('[data-cart-count]').forEach((el) => {
              el.textContent = cart.item_count;
              el.classList.toggle('visually-hidden', cart.item_count === 0);
            });
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
            window.setTimeout(() => {
              btn.textContent = original;
              btn.classList.remove('is-added');
              btn.disabled = false;
            }, 1600);
          })
          .catch((err) => {
            console.error('[pdp] rec add failed', err);
            btn.textContent = tProd.error || 'Error';
            window.setTimeout(() => {
              btn.textContent = original;
              btn.disabled = false;
            }, 2000);
          });
      });
    });

    // ------------------------------------------------------------------
    // Gallery progress bar (mobile)
    // ------------------------------------------------------------------
    const gallery = section.querySelector('[data-gallery]');
    const segments = section.querySelectorAll('[data-progress-segment]');
    if (gallery && segments.length > 1 && 'IntersectionObserver' in window) {
      const slides = gallery.querySelectorAll('[data-gallery-slide]');
      const setActive = (idx) => {
        segments.forEach((seg, i) => seg.classList.toggle('is-active', i === idx));
      };
      const observer = new IntersectionObserver(
        (entries) => {
          // Use the entry with highest intersectionRatio.
          let best = null;
          entries.forEach((entry) => {
            if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
          });
          if (best && best.isIntersecting) {
            const idx = Array.from(slides).indexOf(best.target);
            if (idx >= 0) setActive(idx);
          }
        },
        { root: gallery, threshold: [0.5, 0.75, 1] }
      );
      slides.forEach((s) => observer.observe(s));
    }

    // ------------------------------------------------------------------
    // Recs horizontal scroll progress bar
    // ------------------------------------------------------------------
    const recsList = sectionScope.querySelector('[data-recs-list]');
    const recsBar = sectionScope.querySelector('[data-recs-progress-bar]');
    if (recsList && recsBar) {
      const updateRecsProgress = () => {
        const max = recsList.scrollWidth - recsList.clientWidth;
        if (max <= 0) {
          recsBar.style.left = '0%';
          recsBar.style.width = '100%';
          return;
        }
        const ratio = recsList.clientWidth / recsList.scrollWidth;
        const progress = recsList.scrollLeft / max;
        const barWidth = ratio * 100;
        const barLeft = (1 - ratio) * progress * 100;
        recsBar.style.width = `${barWidth}%`;
        recsBar.style.left = `${barLeft}%`;
      };
      recsList.addEventListener('scroll', updateRecsProgress, { passive: true });
      window.addEventListener('resize', updateRecsProgress);
      updateRecsProgress();
    }

    // ------------------------------------------------------------------
    // Zoom modal
    // ------------------------------------------------------------------
    const zoomModal = section.querySelector('[data-zoom-modal]');
    if (zoomModal) {
      const zoomImage = zoomModal.querySelector('[data-zoom-image]');
      const zoomContainer = zoomModal.querySelector('[data-zoom-scroll]');

      const openZoom = (src, alt) => {
        if (!zoomImage) return;
        zoomImage.src = src;
        zoomImage.alt = alt || '';
        zoomModal.classList.add('is-open');
        zoomModal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('pdp-zoom-open');
      };

      const closeZoom = () => {
        zoomModal.classList.remove('is-open');
        zoomModal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('pdp-zoom-open');
        if (zoomContainer) zoomContainer.classList.remove('is-zoomed');
      };

      section.querySelectorAll('[data-zoom-trigger]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          openZoom(btn.dataset.zoomSrc, btn.dataset.zoomAlt);
        });
      });

      zoomModal.querySelectorAll('[data-zoom-close]').forEach((c) =>
        c.addEventListener('click', closeZoom)
      );

      // Click image/background to toggle zoom level (desktop).
      if (zoomContainer) {
        zoomContainer.addEventListener('click', (e) => {
          // Clicking the image or the container toggles zoom.
          if (e.target === zoomContainer) {
            closeZoom();
          } else if (e.target === zoomImage) {
            zoomContainer.classList.toggle('is-zoomed');
          }
        });
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && zoomModal.classList.contains('is-open')) closeZoom();
      });
    }

    // ------------------------------------------------------------------
    // Size guide modal
    // ------------------------------------------------------------------
    const sizeGuideModal = section.querySelector('[data-size-guide-modal]');
    if (sizeGuideModal) {
      const triggers = section.querySelectorAll('[data-size-guide]');
      const closers = sizeGuideModal.querySelectorAll('[data-size-guide-close]');
      const open = (e) => {
        if (e) e.preventDefault();
        sizeGuideModal.classList.add('is-open');
        sizeGuideModal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('pdp-size-guide-open');
      };
      const close = () => {
        sizeGuideModal.classList.remove('is-open');
        sizeGuideModal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('pdp-size-guide-open');
      };
      triggers.forEach((t) => t.addEventListener('click', open));
      closers.forEach((c) => c.addEventListener('click', close));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && sizeGuideModal.classList.contains('is-open')) close();
      });

      const unitButtons = sizeGuideModal.querySelectorAll('[data-unit-toggle]');
      if (unitButtons.length) {
        unitButtons.forEach((btn) => {
          btn.addEventListener('click', () => {
            const unit = btn.getAttribute('data-unit-toggle');
            sizeGuideModal.setAttribute('data-unit', unit);
            unitButtons.forEach((b) => {
              const active = b === btn;
              b.classList.toggle('is-active', active);
              b.setAttribute('aria-pressed', active ? 'true' : 'false');
            });
          });
        });
      }

      // ----- Master size guide (Susmies-style: tabs por categoría + cm/in)
      const masterGuide = sizeGuideModal.querySelector('[data-iurop-size-guide]');
      if (masterGuide) {
        const tabs = masterGuide.querySelectorAll('[data-iurop-tab]');
        const contents = masterGuide.querySelectorAll('[data-iurop-content]');
        const measureUnits = masterGuide.querySelectorAll('[data-iurop-unit]');

        tabs.forEach((tab) => {
          tab.addEventListener('click', () => {
            const id = tab.getAttribute('data-iurop-tab');
            tabs.forEach((t) => {
              const active = t === tab;
              t.classList.toggle('is-active', active);
              t.setAttribute('aria-selected', active ? 'true' : 'false');
            });
            contents.forEach((c) => {
              c.classList.toggle('is-active', c.getAttribute('data-iurop-content') === id);
            });
          });
        });

        measureUnits.forEach((btn) => {
          btn.addEventListener('click', () => {
            const unit = btn.getAttribute('data-iurop-unit');
            masterGuide.setAttribute('data-unit', unit);
            measureUnits.forEach((b) => {
              const active = b === btn;
              b.classList.toggle('is-active', active);
              b.setAttribute('aria-pressed', active ? 'true' : 'false');
            });
            masterGuide.querySelectorAll('[data-iurop-measure]').forEach((m) => {
              m.classList.toggle('is-active', m.getAttribute('data-iurop-measure') === unit);
            });
          });
        });
      }
    }
  };

  const boot = () => {
    document.querySelectorAll('[data-product-section]').forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const s = e.target.querySelector('[data-product-section]');
    if (s) initSection(s);
  });
})();
